import { useApiClient } from "../../../hooks/useApiClient";
import { toast } from "react-toastify";
import { isAxiosError, type AxiosResponse } from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChangePasswordFormSchema,
  type ChangePasswordFormData,
} from "../types/schemas";

interface ChangePasswordFormResponse {
  currentPassword: string[];
  newPassword: string[];
  reNewPassword: string[];
  nonFieldErrors: string[];
}

interface UseChangePasswordFormReturn {
  form: ReturnType<typeof useForm<ChangePasswordFormData>>;
  handleSubmit: (data: ChangePasswordFormData) => Promise<void>;
}

export const useChangePasswordForm = (): UseChangePasswordFormReturn => {
  const { apiClient } = useApiClient();

  const form = useForm<ChangePasswordFormData>({
    mode: "all",
    criteriaMode: "all", // 複数のエラーを同時に表示するための設定
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      newPasswordConfirmation: "",
    },
    resolver: zodResolver(ChangePasswordFormSchema),
  });

  const handleSubmit = async (data: ChangePasswordFormData): Promise<void> => {
    try {
      const response = await apiClient.post<
        AxiosResponse<ChangePasswordFormResponse>
      >("auth/users/set_password/", {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        reNewPassword: data.newPasswordConfirmation,
      });

      // 204以外のステータスコードが返ってきた場合、想定外のためエラーを投げる
      if (response.status !== 204) {
        throw new Error();
      }

      form.reset();
      toast.success("パスワードを変更しました");
    } catch (error) {
      if (!isAxiosError<ChangePasswordFormResponse>(error)) {
        toast.error("パスワードの変更に失敗しました");
        return;
      }
      if (error.response?.status === 400) {
        const data = error.response?.data;

        // types.requiredにlist[str]を指定することで、複数のエラーを同時に表示できる
        if (data.currentPassword) {
          form.setError("currentPassword", {
            types: {
              required: data.currentPassword,
            },
          });
        }
        if (data.newPassword) {
          form.setError("newPassword", {
            types: {
              required: data.newPassword,
            },
          });
        }
        if (data.nonFieldErrors) {
          form.setError("newPassword", {
            types: {
              required: data.nonFieldErrors,
            },
          });
        }
      }
      // 想定外のステータスコードの場合、エラーメッセージを表示する
      else {
        toast.error("パスワードの変更に失敗しました");
      }
    }
  };

  return {
    form,
    handleSubmit,
  };
};
